import { useCallback, useState } from 'react';
import type { SubmitEvent } from 'react';
import { useTranslations } from 'next-intl';
import { analytics } from '@silvertours/front-entities';
import type { http } from '@silvertours/front-shared';
import Search from '@engineering/icons/search';
import { SearchInput } from './SearchInput';
import { SearchProvider, useSearchContext } from './context';
import { useSession } from './hooks';
import { matchError, useSubmitHandler } from './hooks/useSubmitHandler';

import { isOneWay } from '../SearchForm';
import {
  Wrapper,
  Title,
  Form,
  FormContainer,
  StyledButton,
  StyledDateRangePicker,
} from './WidgetSearchForm.styles';
import { isFormError } from './context/types';

const WidgetSearchForm = () => {
  const { setDate, setError, state } = useSearchContext();

  const t = useTranslations('features.stageLegacy.stage.searchForm');
  const submitHandler = useSubmitHandler();

  const { trackClickTimestamp, removeClickTimestamp } = useSession();
  const [loading, setLoading] = useState(false);

  const onError = useCallback((error: http.AxiosError) => {
    setLoading(false);
    removeClickTimestamp();

    const { type: kind, message } = matchError(error);

    if (isFormError(kind)) {
      setError({ kind, message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = async () => {
    setLoading(true);
    trackClickTimestamp();
    await submitHandler(
      state,
      '',
      () => {
        setLoading(false);
        analytics.gtm.trackSearchSubmit(isOneWay(state));
      },
      onError,
      true,
    );
  };

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSearchSubmit();
  };

  if (!state) {
    return null;
  }

  return (
    <Wrapper id="widget-search">
      <Title>{t('title')}</Title>
      <Form onSubmit={handleSubmit} id="search" role="search" target="_blank">
        <FormContainer>
          <SearchInput
            id="dep"
            direction="departure"
            onServiceNumbersSubmit={handleSearchSubmit}
            label={t('departure.label')}
            placeholder={t('departure.placeholder')}
            icon={<Search />}
          />
          <StyledDateRangePicker
            from={new Date(state.depDate)}
            to={new Date(state.destDate)}
            error={state.dateError}
            onDatesChange={dates => {
              setDate('departure', dates.from!);
              setDate('destination', dates.to!);
            }}
            size="small"
            vertical
          />
          <div>
            <StyledButton
              type="submit"
              id="find"
              size="small"
              loading={loading}
              fullWidth
            >
              {t('button.label')}
            </StyledButton>
          </div>
        </FormContainer>
      </Form>
    </Wrapper>
  );
};

const WidgetSearchFormWithContext = ({ ...formProps }) => (
  <SearchProvider>
    <WidgetSearchForm {...formProps} />
  </SearchProvider>
);

export { WidgetSearchFormWithContext as WidgetSearchForm };
